import { digitalLeadsLabel } from '../../../../../lib/portalReport/config';
import { useState, type ReactNode } from 'react';
import type {
  PortalAdGroupRow,
  PortalCampaignRow,
  PortalCombinedCampaignRow,
  PortalDelta,
  PortalLocationRow,
  PortalReportLabels,
  PortalSearchTermRow,
} from '../../../../../types/portalReport';
import {
  TERM_FILTER_OPTIONS,
  filterByPlatform,
  filterTerms,
  sortReportRows,
  type PlatformFilter,
  type SortDirection,
  type TermFilter,
} from '../../../../../lib/portalReport/reportTable';
import { SwitchGroup } from './controls';
import { DASH, moneyFull, moneyRound, numberFull } from './format';
import { buildLocationDeltas, portalDelta } from '../../../../../lib/portalReport/deltas';
import {
  leaderboardCoverageNote,
  leaderboardCrmNote,
  leaderboardNestingNote,
  leaderboardPartialNote,
} from '../../../../../lib/portalReport/reportCopy';
import { groupAverageDenominator } from '../../../../../lib/portalReport/reportCopy';
import { locationCostPerSale, type GroupAverage } from '../../../../../lib/portalReport/locations';
import { portalReportHref } from '../../../../../lib/portalNav';
import { Figure } from './controls';

// CLIENT PORTAL RESTYLE — the report's tables.
//
// ⚠️⚠️ SALES, COST PER SALE AND CONVERSION RATE ARE OMITTED FROM THESE TABLES
// ENTIRELY — not rendered as em dashes. An empty column reads as a measured
// zero, which on a sales column is the worst available misreading.
//
// ⚠️ THEY STAY OMITTED EVEN WITH A CRM CONNECTED (2026-08-31), and that is not
// an oversight: a GymSales sale row carries no source, no contact_type and no
// person_id, so a sale can never be attributed to a CAMPAIGN. Those figures are
// club-level and live in their own panel; putting a column here would invite
// exactly the per-campaign reading the data cannot support.
//
// A note under the campaign table says they arrive with the CRM — shown only
// while that is still true, i.e. to a client with no CRM connected.
//
// ⚠️ Impressions and clicks live HERE and never in a headline (brand rule 7).

export interface Column<T> {
  key: Extract<keyof T, string>;
  label: string;
  align?: 'left';
  render: (row: T) => ReactNode;
}

export function SortableTable<T>({
  rows,
  columns,
  initialSort,
  caption,
  rowKey,
  rowClassName,
}: {
  rows: readonly T[];
  columns: ReadonlyArray<Column<T>>;
  initialSort: Extract<keyof T, string>;
  caption: string;
  rowKey: (row: T, index: number) => string;
  /** Optional per-row class (the website comparison's selected-studio highlight). */
  rowClassName?: (row: T) => string | undefined;
}) {
  const [sortKey, setSortKey] = useState<Extract<keyof T, string>>(initialSort);
  const [direction, setDirection] = useState<SortDirection>('desc');
  const sorted = sortReportRows(rows, sortKey, direction);

  return (
    <div className="rpt-table-wrap">
      <table className="rpt-table">
        <caption className="rpt-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                aria-sort={
                  sortKey === c.key ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
                }
                onClick={() => {
                  if (sortKey === c.key) setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                  else {
                    setSortKey(c.key);
                    setDirection('desc');
                  }
                }}
              >
                {c.label}
                {sortKey === c.key && (
                  <span className="rpt-sortmark">{direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={rowKey(row, i)} className={rowClassName?.(row)}>
              {columns.map((c) => (
                <td key={c.key} className={c.align === 'left' ? undefined : 'rpt-num'}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * ALL LOCATIONS — the leaderboard (§4 phase A, 2026-09-03).
 *
 * Scott, 2026-09-02: "a report the managers and owners use to get a snapshot on
 * the overall results. If they need further detail, they then go into the
 * individual report."
 *
 * ⚠️⚠️ EVERY CLUB IN SCOPE GETS A ROW, including one with no delivery in the
 * period. The per-account query returns only clubs that HAVE rows, so before
 * this a manager looking for a quiet club found nothing at all — which reads as
 * a broken report rather than as a quiet month. `buildLocationRows` seeds the
 * rows from the scope.
 *
 * ⚠️⚠️ A CLUB THE PERIOD DID NOT MEASURE SHOWS DASHES, NOT ZEROS, and the note
 * beneath NAMES those clubs. One combined series has one coverage; twelve clubs
 * have twelve.
 *
 * ⚠️⚠️ THE CRM COLUMNS ARE THE OVERVIEW'S ALONE. They appear when a row actually
 * carries `crm`, which the server sets on the Overview only — a sale cannot be
 * attributed to a channel, so a sales column sitting in a row of Meta's spend
 * and leads would be read as Meta's sales.
 */
export function LocationsTable({
  rows,
  labels,
  tab,
  uncountedCrmClubs = [],
}: {
  rows: PortalLocationRow[];
  labels: PortalReportLabels;
  /**
   * The page the reader is ON, so a club link lands on the same tab (§7).
   *
   * ⚠️ REQUIRED, not defaulted to the overview: a manager clicking a club from
   * the Meta page wants that club's META page, and a silent default would send
   * every one of them to the combined view instead.
   */
  tab: 'overview' | 'meta' | 'google';
  /**
   * Clubs the CRM does not cover, NAMED. ⚠️ From `PortalCrmBlock.uncountedClubs`
   * — the payload already carries them precisely so an unlinked club is named
   * rather than summing in as a zero.
   */
  uncountedCrmClubs?: readonly string[];
}) {
  // ⚠️ The share denominator sums only what was MEASURED. An unmeasured club's
  // null contributes nothing, which is right — but its own share is a separate
  // null, because "we cannot say" is not "none of them".
  const total = rows.reduce((sum, r) => sum + (r.leads ?? 0), 0);
  const showCrm = rows.some((r) => r.crm !== null);
  const withDerived = rows.map((r) => ({
    ...r,
    d: buildLocationDeltas(r),
    share: r.leads === null || total <= 0 ? null : (r.leads / total) * 100,
    // ⚠ FLATTENED so the CRM columns are SORTABLE: `Column.key` has to be a
    // key of the row, and `sortReportRows` compares that key's value — a nested
    // `crm` object would sort by object identity, i.e. not at all.
    // ⚠ `null`, never 0, for a club the CRM does not cover, so it sorts as
    // absent rather than as the worst-performing club in the group.
    crmLeads: r.crm?.leads ?? null,
    crmDigitalLeads: r.crm?.digitalLeads ?? null,
    crmSales: r.crm?.sales ?? null,
    // ⚠️ Flattened so the column can SORT, like the CRM counts above it — and
    // null for a club the CRM does not cover, so it sorts as absent rather than
    // as the cheapest club in the group.
    costPerSale: locationCostPerSale(r),
  }));
  type Row = (typeof withDerived)[number];

  const money = (v: number | null, currency: string | null): ReactNode =>
    v === null || currency === null ? DASH : moneyRound(v, currency);
  const cost = (v: number | null, currency: string | null): ReactNode =>
    v === null || currency === null ? DASH : moneyFull(v, currency);
  const count = (v: number | null | undefined): ReactNode =>
    v === null || v === undefined ? DASH : numberFull(v);

  // ⚠️ The metric is no longer passed: without a tone colour there is nothing
  // for it to decide. See `LocationDelta`.
  const cell = (value: ReactNode, delta: PortalDelta | null): ReactNode => (
    <>
      {value}
      <LocationDelta delta={delta} />
    </>
  );

  const unmeasured = rows.filter((r) => r.coverage === 'no-data').map((r) => r.accountName);
  const partial = rows.filter((r) => r.coverage === 'partial').map((r) => r.accountName);

  return (
    <>
      {/*
        ⚠️ THE NESTING NOTE LEADS THE TABLE. A caveat read after a number has been
        believed is not a caveat — the same rule the CRM panel and the audience
        panel follow.
      */}
      <p className="rpt-sub rpt-muted">{leaderboardNestingNote(labels)}</p>
      <SortableTable
        caption="Every location on this login, for the selected period"
        rows={withDerived}
        rowKey={(r) => r.accountId}
        initialSort="leads"
        columns={[
          {
            key: 'accountName',
            label: 'Location',
            align: 'left',
            // ⚠️ THE CLUB NAME IS A LINK TO THAT CLUB'S REPORT (§7,
            // 2026-09-03), on the page the reader is ALREADY ON: a manager
            // clicking a club from the Meta page wants that club's Meta page.
            //
            // ⚠️ Through `portalReportHref`, the same builder the left nav
            // uses — a second way of writing this URL is a second way for it
            // to drift from the one `useReportState` reads.
            render: (r: Row) => (
              <>
                <a className="rpt-loc-link" href={portalReportHref(r.accountId, tab)}>
                  {r.accountName}
                </a>
                {r.coverage === 'partial' && <span className="rpt-mono rpt-muted"> · partial</span>}
              </>
            ),
          },
          /*
            ⚠️⚠️ THE COLUMN ORDER IS SETTLED (Scott, 2026-09-03) AND IT IS NOT A
            TIDINESS QUESTION. The counts read left to right as a funnel — spend,
            then what the ads returned, then what the club received, then what it
            sold — and the two EFFICIENCY figures sit together at the end.
            ⚠️ Do NOT "tidy" cost per lead back next to Ad Conversions: grouping
            the two costs is the reason they are last.
          */
          {
            key: 'spend',
            label: 'Spend',
            render: (r: Row) => cell(money(r.spend, r.currency), r.d.spend),
          },
          {
            key: 'leads',
            // ⚠️ FROM `labels`, which now says "Ad Conv." — the ad platforms'
            // own conversion counts, not the club's enquiries. Never a literal
            // here; the whole point of the rename is one name everywhere.
            label: labels.leadsShort,
            render: (r: Row) => cell(count(r.leads), r.d.leads),
          },
          ...(showCrm
            ? [
                {
                  key: 'crmDigitalLeads' as const,
                  label: digitalLeadsLabel(labels),
                  render: (r: Row) => cell(count(r.crmDigitalLeads), r.d.crmDigitalLeads),
                },
                {
                  key: 'crmLeads' as const,
                  label: 'Total leads',
                  render: (r: Row) => cell(count(r.crmLeads), r.d.crmLeads),
                },
                {
                  key: 'crmSales' as const,
                  label: labels.sales,
                  render: (r: Row) => cell(count(r.crmSales), r.d.crmSales),
                },
              ]
            : []),
          {
            key: 'costPerLead',
            label: labels.costPerLead,
            render: (r: Row) => cell(cost(r.costPerLead, r.currency), r.d.costPerLead),
          },
          ...(showCrm
            ? [
                {
                  key: 'costPerSale' as const,
                  label: labels.costPerSale,
                  // ⚠️ A CLUB THE CRM DOES NOT COVER SHOWS A DASH, never a ratio
                  // computed against an assumed zero — `locationCostPerSale`
                  // refuses it rather than this cell remembering to.
                  render: (r: Row) => cost(r.costPerSale, r.currency),
                },
              ]
            : []),
        ]}
      />
      {/*
        ⚠️ TWO DIFFERENT SENTENCES, never merged. "We were not measuring your
        advertising" and "this club is not linked to the club system" send a
        reader to two different people to fix two different things.
      */}
      {unmeasured.length > 0 && (
        <p className="rpt-sub rpt-muted">{leaderboardCoverageNote(unmeasured)}</p>
      )}
      {partial.length > 0 && (
        <p className="rpt-sub rpt-muted">{leaderboardPartialNote(partial)}</p>
      )}
      {showCrm && uncountedCrmClubs.length > 0 && (
        <p className="rpt-sub rpt-muted">{leaderboardCrmNote(uncountedCrmClubs)}</p>
      )}
    </>
  );
}

/**
 * A row's change, under its figure.
 *
 * ⚠️⚠️ NO TONE COLOUR, AND THAT IS A MEASURED DECISION RATHER THAN A DESIGN
 * PREFERENCE. `.rpt-tone-good` / `.rpt-tone-bad` are FIXED values —
 * `--rpt-kelly` #17b057 and `--rpt-error` #ff6b5e, identical in both modes —
 * chosen for the hero band, which is `ink-deep` in both modes. On this
 * leaderboard's ORDINARY band they land on cream, and I measured them in a
 * browser at **2.48:1 and 2.44:1**: not low contrast, unreadable.
 *
 * CLAUDE.md already records this as a latent defect and names the trigger
 * exactly — "what keeps it latent is that `DeltaLine` only ever renders inside a
 * hero band; MOVING ONE IS THE THING TO PREVENT". This leaderboard is that move,
 * so the colour does not come with it.
 *
 * ⚠️ THE DIRECTION SURVIVES WITHOUT IT: an arrow and a TRUE MINUS SIGN, which the
 * hero's `DeltaLine` already relies on for exactly the colour-blind case. What is
 * lost is the good/bad JUDGEMENT — a falling cost per lead reading green — and
 * restoring that on a light surface needs a validated light-surface pair, which
 * is its own pass across every band the chip could land on. §7.2 of the
 * 2026-09-03 brief: report it, do not invent a token or a hex here.
 *
 * ⚠️ `rpt-delta` IS KEPT for its layout (a block on its own line under the
 * figure, 7px up); only the tone class is dropped, for `rpt-muted`, which is
 * mode-aware and measured 5.29:1 light / 7.86:1 dark.
 */
export function LocationDelta({ delta }: { delta: PortalDelta | null }) {
  if (!delta) return null;
  const arrow = delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '■';
  const sign = delta.pct < 0 ? '−' : '+';
  return (
    <span className="rpt-delta rpt-muted">
      {arrow} {sign}
      {Math.abs(delta.pct).toFixed(0)}%
    </span>
  );
}

/**
 * The two group-average tiles (§4c, 2026-09-03).
 *
 * ⚠️⚠️ EACH TILE STATES ITS DENOMINATOR, and the denominator is the clubs WITH
 * DATA rather than the clubs in scope. A club that has never run ads dragging
 * the average down is rule 15 at aggregate scale — an unmeasured thing counted
 * as a measured nothing — and an average whose divisor is invisible cannot be
 * checked by the person reading it.
 *
 * ⚠️⚠️ COUNTS ONLY. There is deliberately no average spend and no average cost
 * per lead: an ANZ group would produce a figure that exists in no currency at
 * all. If a single-currency scope ever makes one tempting, the `A$` / `NZ$`
 * disambiguation is a prerequisite of that work, not a follow-up.
 *
 * ⚠️ THE SALES TILE IS ABSENT WITHOUT A CRM, rather than showing a dash. A
 * labelled empty tile reads as a broken figure; no tile reads as a section that
 * does not apply — the same call `ReportTables` makes about the sales column.
 */
export function GroupAverageTiles({
  averages,
  labels,
  showSales,
}: {
  averages: { leads: GroupAverage; sales: GroupAverage };
  labels: PortalReportLabels;
  showSales: boolean;
}) {
  const tiles: Array<{ label: string; avg: GroupAverage }> = [
    { label: `${labels.leadsShort} per club`, avg: averages.leads },
  ];
  if (showSales) tiles.push({ label: `${labels.sales} per club`, avg: averages.sales });
  return (
    <div className="rpt-card-grid rpt-card-grid--3">
      {tiles.map((t) => (
        <div key={t.label} className="rpt-card">
          <span className="rpt-mono">{t.label}</span>
          {/* ⚠️⚠️ `rpt-card-figure`, NOT `rpt-stat-figure`. The latter is a
              HERO-BAND treatment that hardcodes `var(--rpt-kelly)`, and its own
              comment in index.css says so: "kelly on ink-deep is fine at this
              size. It would NOT be at body size on a light card." Measured on
              this band: 2.48:1. `rpt-card-figure` reads the mode-aware
              `--rpt-fig` and already handles the dark-band case, which is
              exactly the treatment a card on an ordinary band wants.

              ⚠️ One decimal place: an average of 12.4 leads per club is a real
              average, and rounding to 12 would make two different groups look
              identical. */}
          <p className="rpt-card-figure">
            {t.avg.value === null ? DASH : t.avg.value.toFixed(1)}
          </p>
          <LocationDelta
            delta={
              t.avg.value !== null && t.avg.previous !== null
                ? portalDelta(t.avg.value, t.avg.previous)
                : null
            }
          />
          <p className="rpt-sub rpt-muted">
            {groupAverageDenominator(t.avg.clubs, t.avg.clubsInScope)}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * The Overview's combined campaign table.
 *
 * ⚠️ The platform BADGE is a word, not a logo. Meta's partner guidance requires
 * prior Brand Review for any use of its assets and Google requires approved
 * artwork; naming the platform in words is referential use and clears both.
 * ⚠️ And it is a MUTED badge, not a volt one — the mockup caught a volt badge
 * repeating on every Meta row, which broke the one-volt-per-page rule.
 */
export function CombinedCampaignsTable({
  rows,
  labels,
  showsGoogle,
}: {
  rows: PortalCombinedCampaignRow[];
  labels: PortalReportLabels;
  /**
   * ⚠️⚠️ FALSE DROPS THE PLATFORM COLUMN AND THE PLATFORM FILTER, not just the
   * Google rows (the server already excluded those). A one-value filter is
   * chrome pretending to be a control, and a badge reading "Meta" on every row
   * is a column that answers a question nobody can ask.
   *
   * ⚠️ THE SECTION TITLE IS UNCHANGED — "where the money went" is true of one
   * channel.
   */
  showsGoogle: boolean;
}) {
  const [filter, setFilter] = useState<PlatformFilter>('all');
  const visible = showsGoogle ? filterByPlatform(rows, filter) : rows;
  return (
    <>
      {showsGoogle && (
        <div className="rpt-table-controls">
          <SwitchGroup
            ariaLabel="Filter campaigns by platform"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all' as const, label: 'All' },
              { value: 'meta' as const, label: 'Meta' },
              { value: 'google' as const, label: 'Google' },
            ]}
          />
        </div>
      )}
      <SortableTable
        caption={
          showsGoogle
            ? 'Every campaign that spent in this period, both channels together'
            : 'Every campaign that spent in this period'
        }
        rows={visible}
        rowKey={(r) => `${r.platform}:${r.campaignId}`}
        initialSort="spend"
        columns={[
          {
            key: 'campaignName',
            label: 'Campaign',
            align: 'left',
            render: (r) =>
              showsGoogle ? (
                <>
                  <span className="rpt-badge">{r.platform === 'meta' ? 'Meta' : 'Google'}</span>
                  {r.campaignName}
                </>
              ) : (
                r.campaignName
              ),
          },
          { key: 'spend', label: 'Spend', render: (r) => moneyRound(r.spend, r.currency) },
          { key: 'leads', label: labels.leadsShort, render: (r) => numberFull(r.leads) },
          {
            key: 'costPerLead',
            label: labels.costPerLead,
            render: (r) => (r.costPerLead === null ? DASH : moneyFull(r.costPerLead, r.currency)),
          },
        ]}
      />
      {/* ⚠️ THE NOTE IS GONE (2026-09-01). It promised figures that would arrive
          with the CRM and then justified their absence — two sentences about
          something not on the page. The columns are still deliberately omitted
          rather than dashed, and a sale still has no campaign; see this file's
          header, which is where that rule now lives in full. */}
    </>
  );
}

/**
 * The AD-GROUP side of "Where the budget goes".
 *
 * ⚠️ Same columns as the campaign table, plus the PARENT CAMPAIGN. "Brand —
 * Exact" means nothing on its own, and a client scanning ad groups across
 * several campaigns needs to know which one each sits in.
 */
export function AdGroupsTable({
  rows,
  labels,
}: {
  rows: PortalAdGroupRow[];
  labels: PortalReportLabels;
}) {
  return (
    <SortableTable
      caption="Ad group detail for the selected period"
      rows={rows}
      rowKey={(r) => r.adGroupId}
      initialSort="spend"
      columns={[
        { key: 'adGroupName', label: 'Ad group', align: 'left', render: (r) => r.adGroupName },
        { key: 'campaignName', label: 'Campaign', align: 'left', render: (r) => r.campaignName },
        { key: 'spend', label: 'Spend', render: (r) => moneyRound(r.spend, r.currency) },
        { key: 'leads', label: labels.leadsShort, render: (r) => numberFull(r.leads) },
        {
          key: 'costPerLead',
          label: labels.costPerLead,
          render: (r) => (r.costPerLead === null ? DASH : moneyFull(r.costPerLead, r.currency)),
        },
        {
          key: 'impressions',
          label: 'Impressions',
          render: (r) => (r.impressions === null ? DASH : numberFull(r.impressions)),
        },
        {
          key: 'clicks',
          label: 'Clicks',
          render: (r) => (r.clicks === null ? DASH : numberFull(r.clicks)),
        },
      ]}
    />
  );
}

export function ChannelCampaignsTable({
  rows,
  labels,
}: {
  rows: PortalCampaignRow[];
  labels: PortalReportLabels;
}) {
  return (
    <SortableTable
      caption="Campaign detail for the selected period"
      rows={rows}
      rowKey={(r) => r.campaignId}
      initialSort="spend"
      columns={[
        { key: 'campaignName', label: 'Campaign', align: 'left', render: (r) => r.campaignName },
        { key: 'spend', label: 'Spend', render: (r) => moneyRound(r.spend, r.currency) },
        { key: 'leads', label: labels.leadsShort, render: (r) => numberFull(r.leads) },
        {
          key: 'costPerLead',
          label: labels.costPerLead,
          render: (r) => (r.costPerLead === null ? DASH : moneyFull(r.costPerLead, r.currency)),
        },
        {
          key: 'impressions',
          label: 'Impressions',
          render: (r) => (r.impressions === null ? DASH : numberFull(r.impressions)),
        },
        {
          key: 'clicks',
          label: 'Clicks',
          render: (r) => (r.clicks === null ? DASH : numberFull(r.clicks)),
        },
      ]}
    />
  );
}

export function SearchTermsTable({
  rows,
  labels,
  capped,
  total,
}: {
  rows: PortalSearchTermRow[];
  labels: PortalReportLabels;
  capped: boolean;
  total: number;
}) {
  const [filter, setFilter] = useState<TermFilter>('all');
  const visible = filterTerms(rows, filter);
  return (
    <>
      <div className="rpt-table-controls">
        <SwitchGroup
          ariaLabel="Filter search terms"
          value={filter}
          onChange={setFilter}
          options={TERM_FILTER_OPTIONS}
        />
      </div>
      <SortableTable
        caption="The actual words typed before a click"
        rows={visible}
        rowKey={(r) => `${r.searchTerm}|${r.matchType ?? ''}`}
        initialSort="spend"
        columns={[
          { key: 'searchTerm', label: 'Search term', align: 'left', render: (r) => r.searchTerm },
          { key: 'matchType', label: 'Match', render: (r) => r.matchType ?? DASH },
          { key: 'impressions', label: 'Impr.', render: (r) => numberFull(r.impressions) },
          { key: 'clicks', label: 'Clicks', render: (r) => numberFull(r.clicks) },
          {
            // ⚠️ BETWEEN Clicks and Cost, deliberately. Left to right the row then
            // reads: how many clicks, what each one cost, what they cost in total
            // — the ratio sits between its numerator and its denominator's
            // neighbour rather than being appended after the outcome columns.
            //
            // ⚠️ "Cost per click", not "CPC". This page is written for clients;
            // the initialism is agency shorthand.
            key: 'costPerClick',
            label: 'Cost per click',
            // ⚠️ Null is a term with impressions and NO clicks — a dash, never
            // $0.00, which would claim the clicks were free.
            render: (r) => (r.costPerClick === null ? DASH : moneyFull(r.costPerClick, r.currency)),
          },
          { key: 'spend', label: 'Cost', render: (r) => moneyRound(r.spend, r.currency) },
          { key: 'leads', label: labels.leadsShort, render: (r) => numberFull(r.leads) },
          {
            key: 'costPerLead',
            label: labels.costPerLead,
            render: (r) => (r.costPerLead === null ? DASH : moneyFull(r.costPerLead, r.currency)),
          },
        ]}
      />
      {/* ⚠️ A truncated list is never presented as the whole picture. */}
      {capped && (
        <p className="rpt-note rpt-muted">
          Showing the {numberFull(rows.length)} highest-spending of {numberFull(total)} search terms
          in this period.
        </p>
      )}
    </>
  );
}
